import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs/Subject';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/forkJoin';
import 'rxjs/add/operator/takeUntil';
import { TranslateService } from '@ngx-translate/core';

import {
  SkillAiDateRangeParams,
  SkillAiStatisticsSummaryResponse,
  SkillAiTimeseriesItem,
  SkillAiTopUserItem,
  SkillAiTopSpecializationItem,
  SkillAiLogsResponse,
  SkillAiLogsQueryParams,
  SkillAiBudgetStatusResponse,
  SkillAiLogItem,
  SkillAiOperation,
} from '../../../models/skill-learning.model';
import { SkillAiStatisticsFacadeService } from '../../../services/skill-ai-statistics-facade.service';
import { SidebarService } from '../../../../../../services/side-bar.service';
import { ToastService } from '../../../../../../services/toast.service';
import { AiTimeseriesMetric } from '../ai-statistics-timeseries/ai-statistics-timeseries.component';

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_TOP_LIMIT = 10;
const DEFAULT_RANGE_DAYS = 6;
const START_OF_DAY_TIME = '00:00:00';
const END_OF_DAY_TIME = '23:59:59';

@Component({
  selector: 'app-ai-statistics-container',
  templateUrl: './ai-statistics-container.component.html',
  styleUrls: ['./ai-statistics-container.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiStatisticsContainerComponent implements OnInit, OnDestroy {

  summary: SkillAiStatisticsSummaryResponse = this.createEmptySummary();
  timeseries: SkillAiTimeseriesItem[] = [];
  topUsers: SkillAiTopUserItem[] = [];
  topSpecializations: SkillAiTopSpecializationItem[] = [];
  budgetStatus: SkillAiBudgetStatusResponse = this.createEmptyBudgetStatus();
  logs: SkillAiLogItem[] = [];
  selectedMetric: AiTimeseriesMetric = 'requests';
  isOverviewLoading: boolean = true;
  isLogsLoading: boolean = true;
  hasOverviewError: boolean = false;
  hasLogsError: boolean = false;
  page: number = 1;
  totalLogItems: number = 0;
  readonly pageSize: number = DEFAULT_PAGE_SIZE;

  readonly metricOptions = [
    { value: 'requests', key: 'SKILL_LEARNING_AI_STATS_CHART_METRIC_REQUESTS' },
    { value: 'tokens', key: 'SKILL_LEARNING_AI_STATS_CHART_METRIC_TOKENS' },
    { value: 'cost', key: 'SKILL_LEARNING_AI_STATS_CHART_METRIC_COST' },
  ];

  readonly operationOptions = [
    { value: '', key: 'SKILL_LEARNING_AI_STATS_FILTER_ALL' },
    { value: 'CHAT', key: 'SKILL_LEARNING_AI_STATS_OPERATION_CHAT' },
    { value: 'TEST_GENERATION', key: 'SKILL_LEARNING_AI_STATS_OPERATION_TEST_GENERATION' },
  ];

  readonly successOptions = [
    { value: '', key: 'SKILL_LEARNING_AI_STATS_FILTER_ALL' },
    { value: 'true', key: 'SKILL_LEARNING_AI_STATS_FILTER_SUCCESS' },
    { value: 'false', key: 'SKILL_LEARNING_AI_STATS_FILTER_FAILED' },
  ];

  readonly rangeForm = new FormGroup({
    from: new FormControl(),
    to: new FormControl(),
  });

  readonly logsForm = new FormGroup({
    operation: new FormControl(''),
    success: new FormControl(''),
    model: new FormControl(''),
    userId: new FormControl(''),
    specializationId: new FormControl(''),
  });

  private destroy$ = new Subject<void>();

  constructor(
    private facade: SkillAiStatisticsFacadeService,
    private sidebarService: SidebarService,
    private toastService: ToastService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.sidebarService.setSelectedMenuOption('ai-statistics');
    this.setDefaultRange();
    this.loadPageData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get minToDate(): NgbDateStruct | null {
    return this.rangeForm.controls['from'].value || null;
  }

  get maxFromDate(): NgbDateStruct | null {
    return this.rangeForm.controls['to'].value || null;
  }

  get canApplyRange(): boolean {
    const range = this.buildDateRangeParams();
    return !!range;
  }

  onApplyRange(): void {
    if (!this.canApplyRange) {
      return;
    }
    this.loadPageData();
  }

  onResetLogsFilters(): void {
    this.logsForm.reset({
      operation: '',
      success: '',
      model: '',
      userId: '',
      specializationId: '',
    });
    this.loadLogsFirstPage();
  }

  onApplyLogsFilters(): void {
    this.loadLogsFirstPage();
  }

  onTimeseriesMetricSelected(metric: AiTimeseriesMetric): void {
    this.selectedMetric = metric;
    this.cdr.markForCheck();
  }

  onLogsPageChanged(page: number): void {
    this.loadLogs(page);
  }

  trackByUserId(index: number, item: SkillAiTopUserItem): string {
    return item ? item.userId : String(index);
  }

  trackBySpecializationId(index: number, item: SkillAiTopSpecializationItem): string {
    return item ? item.specializationId : String(index);
  }

  trackByLogItem(index: number, item: SkillAiLogItem): string {
    return item && item.id ? item.id : String(index);
  }

  getOperationKey(value: SkillAiOperation): string {
    if (value === 'TEST_GENERATION') {
      return 'SKILL_LEARNING_AI_STATS_OPERATION_TEST_GENERATION';
    }
    return 'SKILL_LEARNING_AI_STATS_OPERATION_CHAT';
  }

  getSuccessKey(success: boolean): string {
    if (success) {
      return 'SKILL_LEARNING_AI_STATS_FILTER_SUCCESS';
    }
    return 'SKILL_LEARNING_AI_STATS_FILTER_FAILED';
  }

  private loadPageData(): void {
    this.loadOverview();
    this.loadLogsFirstPage();
  }

  private loadOverview(): void {
    const range = this.buildDateRangeParams();
    if (!range) {
      return;
    }
    this.isOverviewLoading = true;
    this.hasOverviewError = false;
    this.cdr.markForCheck();
    Observable.forkJoin(
      this.facade.fetchSummary(range),
      this.facade.fetchTimeseries(range),
      this.facade.fetchTopUsers(range, DEFAULT_TOP_LIMIT),
      this.facade.fetchTopSpecializations(range, DEFAULT_TOP_LIMIT),
      this.facade.fetchBudgetStatus(),
    )
      .takeUntil(this.destroy$)
      .subscribe(
        result => this.handleOverviewLoadSuccess(result),
        () => this.handleOverviewLoadError(),
      );
  }

  private loadLogsFirstPage(): void {
    this.loadLogs(1);
  }

  private loadLogs(page: number): void {
    const params = this.buildLogsQuery(page);
    if (!params) {
      return;
    }
    this.isLogsLoading = true;
    this.hasLogsError = false;
    this.cdr.markForCheck();
    this.facade.fetchLogs(params)
      .takeUntil(this.destroy$)
      .subscribe(
        response => this.handleLogsLoadSuccess(response),
        () => this.handleLogsLoadError(),
      );
  }

  private handleOverviewLoadSuccess(result: [
    SkillAiStatisticsSummaryResponse,
    SkillAiTimeseriesItem[],
    SkillAiTopUserItem[],
    SkillAiTopSpecializationItem[],
    SkillAiBudgetStatusResponse
  ]): void {
    this.summary = result[0];
    this.timeseries = result[1];
    this.topUsers = result[2];
    this.topSpecializations = result[3];
    this.budgetStatus = result[4];
    this.isOverviewLoading = false;
    this.hasOverviewError = false;
    this.cdr.markForCheck();
  }

  private handleOverviewLoadError(): void {
    this.isOverviewLoading = false;
    this.hasOverviewError = true;
    this.toastService.error(this.translate.instant('SKILL_LEARNING_AI_STATS_OVERVIEW_ERROR'));
    this.cdr.markForCheck();
  }

  private handleLogsLoadSuccess(response: SkillAiLogsResponse): void {
    this.logs = response.items || [];
    this.page = response.page + 1;
    this.totalLogItems = response.totalItems || 0;
    this.isLogsLoading = false;
    this.hasLogsError = false;
    this.cdr.markForCheck();
  }

  private handleLogsLoadError(): void {
    this.isLogsLoading = false;
    this.hasLogsError = true;
    this.toastService.error(this.translate.instant('SKILL_LEARNING_AI_STATS_LOGS_ERROR'));
    this.cdr.markForCheck();
  }

  private buildLogsQuery(page: number): SkillAiLogsQueryParams | null {
    const range = this.buildDateRangeParams();
    if (!range) {
      return null;
    }
    return {
      from: range.from,
      to: range.to,
      page: page - 1,
      size: this.pageSize,
      operation: this.resolveOperationFilter(),
      success: this.resolveSuccessFilter(),
      model: this.resolveTextControlValue('model'),
      userId: this.resolveTextControlValue('userId'),
      specializationId: this.resolveTextControlValue('specializationId'),
    };
  }

  private buildDateRangeParams(): SkillAiDateRangeParams | null {
    const from = this.rangeForm.controls['from'].value;
    const to = this.rangeForm.controls['to'].value;
    if (!this.isValidRange(from, to)) {
      return null;
    }
    return {
      from: this.formatFromDateValue(from),
      to: this.formatToDateValue(to),
    };
  }

  private resolveOperationFilter(): SkillAiOperation | null {
    const value = this.logsForm.controls['operation'].value;
    if (value === 'CHAT' || value === 'TEST_GENERATION') {
      return value as SkillAiOperation;
    }
    return null;
  }

  private resolveSuccessFilter(): boolean | null {
    const value = this.logsForm.controls['success'].value;
    if (value === 'true') {
      return true;
    }
    if (value === 'false') {
      return false;
    }
    return null;
  }

  private resolveTextControlValue(controlName: string): string {
    const value = this.logsForm.controls[controlName].value;
    return value ? String(value).trim() : '';
  }

  private setDefaultRange(): void {
    const toDate = new Date();
    const fromDate = this.subtractDays(toDate, DEFAULT_RANGE_DAYS);
    this.rangeForm.setValue({
      from: this.toDateStruct(fromDate),
      to: this.toDateStruct(toDate),
    });
  }

  private subtractDays(source: Date, days: number): Date {
    const result = new Date(source.getTime());
    result.setDate(result.getDate() - days);
    return result;
  }

  private toDateStruct(date: Date): NgbDateStruct {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
    };
  }

  private isValidRange(from: NgbDateStruct | null, to: NgbDateStruct | null): boolean {
    if (!this.isDateStruct(from) || !this.isDateStruct(to)) {
      return false;
    }
    return this.compareDates(from, to) <= 0;
  }

  private isDateStruct(value: NgbDateStruct | null): boolean {
    if (!value) {
      return false;
    }
    return !!value.year && !!value.month && !!value.day;
  }

  private compareDates(left: NgbDateStruct, right: NgbDateStruct): number {
    const leftValue = `${left.year}${this.pad(left.month)}${this.pad(left.day)}`;
    const rightValue = `${right.year}${this.pad(right.month)}${this.pad(right.day)}`;
    return leftValue.localeCompare(rightValue);
  }

  private formatFromDateValue(value: NgbDateStruct): string {
    return `${this.formatDatePart(value)}T${START_OF_DAY_TIME}`;
  }

  private formatToDateValue(value: NgbDateStruct): string {
    return `${this.formatDatePart(value)}T${END_OF_DAY_TIME}`;
  }

  private formatDatePart(value: NgbDateStruct): string {
    return `${value.year}-${this.pad(value.month)}-${this.pad(value.day)}`;
  }

  private pad(value: number): string {
    return value < 10 ? `0${value}` : String(value);
  }

  private createEmptySummary(): SkillAiStatisticsSummaryResponse {
    return {
      totalRequests: 0,
      successfulRequests: 0,
      failedRequests: 0,
      totalTokens: 0,
      totalInputTokens: 0,
      totalOutputTokens: 0,
      estimatedCost: 0,
      averageLatencyMs: 0,
      successRate: 0,
      operationBreakdown: [],
      modelBreakdown: [],
    };
  }

  private createEmptyBudgetStatus(): SkillAiBudgetStatusResponse {
    return {
      globalTokensUsed: 0,
      globalTokenLimit: 0,
      usagePercentage: 0,
      status: 'normal',
      testGenerationRequestsToday: 0,
      topUsers: [],
    };
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/common/skill-learning/components/admin/ai-statistics-container/ai-statistics-container.component.ts